/* eslint-disable react/prop-types */
import * as React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Avatar,
  ClickAwayListener,
  useTheme,
  Tooltip,
  tooltipClasses,
  styled,
} from '@mui/material';
import {memo} from 'react';
import {Handle, Position} from 'reactflow';
import {useSelector} from 'react-redux';
import {selectRoleFilters} from 'app/store/appState/appStateSlice';
import {
  SearchRounded,
  ReplyOutlined,
  ModeCommentOutlined,
  AssignmentTurnedInOutlined,
  NotificationsOutlined,
  Create,
  AssignmentOutlined,
  Update,
  EmailOutlined,
  UploadFileOutlined,
  FastForwardOutlined,
  VerifiedOutlined,
  FactCheckOutlined,
  SpeakerNotesOutlined,
  OutboxOutlined,
  HistoryToggleOff,
  Repeat,
} from '@mui/icons-material';
import {UserFilled, WarningAltFilled} from '@carbon/icons-react';
import {selectAllChanges} from 'app/store/change/changeSlice';
import {useFeatureFlag} from 'configcat-react';

const HeaderIcons = (type) => {
  switch (type) {
    case 'Discuss':
      return <ModeCommentOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Review':
      return <SearchRounded fontSize='medium' sx={{color: 'black'}} />;
    case 'Complete':
      return <AssignmentTurnedInOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Receive':
      return <ReplyOutlined fontSize='medium' sx={{transform: 'rotate(-90deg)', color: 'black'}} />;
    case 'Inform':
      return <NotificationsOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Create':
      return <Create fontSize='medium' sx={{color: 'black'}} />;
    case 'Approve':
      return <FactCheckOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Develop':
      return <AssignmentOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Update':
      return <Update fontSize='medium' sx={{color: 'black'}} />;
    case 'Distribute':
      return <EmailOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Upload':
      return <UploadFileOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Version Control':
      return <HistoryToggleOff fontSize='medium' sx={{color: 'black'}} />;
    case 'Promote':
      return <FastForwardOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Confirm':
      return <VerifiedOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Submit':
      return <OutboxOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Justify':
      return <SpeakerNotesOutlined fontSize='medium' sx={{color: 'black'}} />;
    case 'Recurring':
      return <Repeat fontSize='medium' sx={{color: 'black'}} />;
    default:
      return <></>;
  }
};

const transformRoles = (roles) => {
  const responsibleRoles = [];
  const accountableRoles = [];
  let responsibleCount = 0;
  roles.forEach((role) => {
    role.raci_list.split(';').map((r) => {
      if (r === 'R') {
        if (responsibleCount > 4) {
          return;
        }
        if (!responsibleRoles.includes(role.role.role_category.abbrev)) {
          responsibleRoles.push(role.role.role_category.abbrev);
          responsibleCount += 1;
        }
      } else if (r === 'A') {
        if (!accountableRoles.includes(role.role.role_category.abbrev)) {
          accountableRoles.push(role.role.role_category.abbrev);
        }
      }
    });
  });

  const responsible =
    responsibleRoles.length != 0 ? (
      <Stack direction='row' alignItems='center' spacing={1}>
        <Avatar sx={{width: 20, height: 20, bgcolor: '#ABB436'}} color='success'>
          <Typography variant='subtitle2' color='grey.50' sx={{fontWeight: 'bold'}}>
            R
          </Typography>
        </Avatar>
        <Stack direction='row'>
          <Typography variant='h5'>{responsibleRoles.slice(0, 3).join(', ')}</Typography>
          {responsibleRoles.length > 3 && <Typography variant='h5'>...</Typography>}
        </Stack>
      </Stack>
    ) : (
      <></>
    );
  const accountable =
    accountableRoles.length != 0 ? (
      <Stack direction='row' alignItems='center' spacing={1} textOverflow='ellipsis'>
        <Avatar sx={{width: 20, height: 20, bgcolor: '#EA5532'}} color='success'>
          <Typography variant='subtitle2' color='grey.50' sx={{fontWeight: 'bold'}}>
            A
          </Typography>
        </Avatar>
        <Stack direction='row'>
          <Typography variant='h5'>{accountableRoles.slice(0, 2).join(', ')}</Typography>
          {accountableRoles.length > 2 && <Typography variant='h5'>...</Typography>}
        </Stack>
      </Stack>
    ) : (
      <></>
    );

  return (
    <Stack spacing={1} direction='column'>
      {responsible}
      {accountable}
    </Stack>
  );
};

function StepNode({data}) {
  const allChanges = useSelector(selectAllChanges);
  const theme = useTheme();
  const iconStyle = {color: theme.palette.grey[800], margin: 0};
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);

  const ChangeToolTip = styled(({className, ...props}) => <Tooltip {...props} classes={{popper: className}} />)({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: 'white',
      maxWidth: 'none',
      boxShadow: `0px 0px 10px 0px ${theme.palette.grey[700]}`,
    },
  });

  const update = allChanges.some((c) => c.object_key === data.steps?.[0]?.step?.step_id) ? (
    <WarningAltFilled style={iconStyle} />
  ) : (
    <></>
  );
  const roles = transformRoles(data.roles);
  const handleStyle = {transform: 'rotate(0deg)', opacity: '0'};
  const description =
    data.description && data.description.length > 90 ? data.description.slice(0, 90) + '...' : data.description;
  const selectedRoles = useSelector(selectRoleFilters);
  const [selected, setSelected] = React.useState('');

  const hasRole = (roles) => {
    let flag = false;
    roles.map((r) => {
      if (selectedRoles.some((role) => role === r.role.role_category.label)) {
        flag = true;
      }
    });
    return flag;
  };
  const person = hasRole(data.roles) ? (
    <Box minWidth='18px' minHeight='18px'>
      <UserFilled width='18px' height='18px' style={iconStyle} />
    </Box>
  ) : (
    <></>
  );

  const handleSelect = () => {
    if (selected === data.id) {
      setSelected('');
    } else {
      setSelected(data.id);
    }
  };

  const hostName = (id) => {
    const host = window.location.host;
    switch (host) {
      case 'elevate-test.takeda.io':
        return '';
      case 'elevate.takeda.io':
        return '';
      default:
        return id;
    }
  };

  const getUpdate = () => {
    const changes = allChanges.filter((x) => x.object_key === data.steps?.[0]?.step?.step_id);
    if (changes && changes?.length > 0) {
      if (changes.length === 1) {
        const change = changes.at(0);
        return (
          <Stack direction='column' spacing={1}>
            <Stack direction='row' spacing={1} justifyContent='flex-start' alignItems='center'>
              <WarningAltFilled fontSize='small' />
              <Typography>Updates</Typography>
            </Stack>
            {change.length === 1 ? (
              <Typography pl={2}>{change.label}</Typography>
            ) : (
              <Box pl={2}>
                {changes.map((c) => (
                  <li key={`Change--${c.id}`}>{`${c.label}`}</li>
                ))}
              </Box>
            )}
          </Stack>
        );
      }
    }
  };

  return (
    <>
      <ClickAwayListener onClickAway={() => setSelected('')}>
        <Box
          width={'250px'}
          height={'200px'}
          display='flex'
          justifyContent='center'
          alignItems='center'
          onClick={() => handleSelect()}
        >
          <Stack direction='column' spacing={0.5} justifyContent='flex-start'>
            <Stack direction='row' spacing={1} alignItems='center' width='100%' justifyContent='flex-start'>
              {HeaderIcons(data.header)}
              <Typography variant='h5'>{data.header}</Typography>
            </Stack>

            <Paper
              variant='outlined'
              sx={{
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[500]}`,
                  boxShadow: ' 0px 0px 10px rgba(0, 0, 0, 0.25)',
                },
                border:
                  selected === data.id
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.grey[500]}`,
                width: '250px',
                height: '180px',
                borderRadius: '6px',
              }}
            >
              <Stack spacing={1} p={'10px'}>
                <Stack spacing={1} direction='row' justifyContent='flex-end' alignItems='center' minHeight={'24px'}>
                  {person}
                  {isChangeNotificationsEnabled && (
                    <ChangeToolTip
                      title={
                        <Typography variant='h5' color={theme.palette.grey[800]}>
                          {getUpdate()}
                        </Typography>
                      }
                    >
                      {update}
                    </ChangeToolTip>
                  )}
                </Stack>
                <Box minHeight={65}>
                  <Typography variant='h5'>{description}</Typography>
                </Box>
                <Stack direction='row' justifyContent='flex-end' alignItems='flex-end'>
                  {roles}
                  <Box flexGrow={1} display='flex' justifyContent='flex-end'>
                    <Typography variant='h5'>{hostName(data.id)}</Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      </ClickAwayListener>

      <Handle id='leftSource' style={handleStyle} type='source' position={Position.Left} />
      <Handle id='topSource' style={handleStyle} type='source' position={Position.Top} />
      <Handle id='rightSource' style={handleStyle} type='source' position={Position.Right} />
      <Handle id='bottomSource' style={handleStyle} type='source' position={Position.Bottom} />

      <Handle id='leftTarget' style={handleStyle} type='target' position={Position.Left} />
      <Handle id='topTarget' style={handleStyle} type='target' position={Position.Top} />
      <Handle id='rightTarget' style={handleStyle} type='target' position={Position.Right} />
      <Handle id='bottomTarget' style={handleStyle} type='target' position={Position.Bottom} />
    </>
  );
}

export default memo(StepNode);
